import { Statistic } from '@helpers/statistics';
import {
  IForm,
  IResponsePrepare,
  IStation,
  IVehiclesCapacities,
  IVersion,
  IZone,
  IZoneArea,
  IZoneSelector,
  InductionRoute,
  Simulation,
  Version,
  Zone,
  algorithms,
  calculation_mode,
  sizes_enum,
  universe_mode,
} from '@helpers/types';
import * as turf from '@turf/turf';
import { AxiosInstance } from 'axios';
import { endOfDay, startOfDay } from 'date-fns';
import { MutableRefObject, useCallback } from 'react';

export interface LastMileRequest {
  // From cluster.ts
  getVersionsSettings: (
    station: string,
    country: string,
  ) => Promise<Array<IVersion>>;
  saveMap: (
    version: string,
    station: string,
    country: string,
    file: File,
  ) => Promise<any>;
  saveVersionSettings: (
    version: string,
    station: string,
    country: string,
    createEmpty: boolean,
    cloneVersion: string,
  ) => Promise<any>;
  getZonesSettings: (
    country: string,
    station: string,
    versionId: string | null,
  ) => Promise<Array<IZoneArea>>;
  storeZones: (
    country: string,
    station: string,
    areas: Array<IZoneArea>,
    versionId: string | null,
  ) => Promise<void>;
  findZone: (
    country: string,
    station: string,
    versionId: string | null,
  ) => Promise<Array<IZoneSelector>>;
  prepareKmeans: (kmeans: IForm, email: string) => Promise<IResponsePrepare>;
  calculateKmeans: (keans: IForm) => Promise<IResponsePrepare>;
  calculateOrTools: (keans: IForm) => Promise<IResponsePrepare>;
  getKmeans: (date: Date, station: IStation) => Promise<any>;
  getPlanSummary: (plan_id: string) => Promise<any>;
  approvePlanSummary: (
    plan_id: string,
    approve: boolean,
  ) => Promise<{ data: any; error: boolean }>;
  initOSRMService: () => Promise<void>;
  prepareForInduction: (plans: Array<string>) => Promise<any>;

  // From version.ts
  getVersions: (
    country: string,
    station: string,
    includeInactive?: boolean,
  ) => Promise<Array<Version>>;
  disableVersion: (versionId: string) => Promise<any>;
  enableVersion: (versionId: string) => Promise<any>;
  deleteVersion: (versionId: string) => Promise<any>;
  getZones: (versionId: string) => Promise<Array<Zone>>;
  deleteZone: (zoneId: string) => Promise<any>;
  disolveZones: (zones: Array<string>, name: string) => Promise<any>;
  createVersion: (
    name: string,
    file: File,
    station: string,
    country: string,
  ) => Promise<any>;

  // From routing.ts
  getRoutingVerification: (
    plan_id: string,
    plan_name: string,
    prefix: string,
    station: string,
    country: string,
    mode: calculation_mode,
    universe: Array<universe_mode>,
    sizes: Array<sizes_enum>,
    version: IVersion,
    vehicles: Partial<IVehiclesCapacities>,
    file: File | null,
    simulationTime: number,
    routeTime: number,
    fusion: number,
    algorithm: algorithms,
    max_distance?: number,
    service_time?: number,
    preset?: string,
  ) => Promise<Statistic>;
  prepareSimulation: (plan_id: string) => Promise<any>;
  startSimulation: (plan_id: string, user: string) => Promise<any>;
  startKSimulation: (plan_id: string, user: string) => Promise<any>;
  cleanUp: (plan_id: Array<string>) => Promise<any>;
  acceptPlan: (plan_id: string, station: string) => Promise<any>;
  rejectPlan: (plan_id: string) => Promise<any>;
  getPlan: (plan_id: string) => Promise<Simulation>;
  getPlans: (pagination: any) => Promise<any>;
  getRoutes: (simulations: Array<string>) => Promise<Array<InductionRoute>>;
  setInduct: (
    plan_id: string,
    route: string,
    tracking_id: number,
  ) => Promise<Array<InductionRoute>>;
  getInductProgress: (
    plan_id: Array<string>,
    routes: Array<string>,
  ) => Promise<Array<InductionRoute>>;
  postSplitRoute: (
    planId: string,
    routeId: string,
    vehicles: Partial<IVehiclesCapacities>,
  ) => Promise<any>;
  postJoinRoutes: (planId: string, routeIds: Array<string>) => Promise<any>;
  postDisableRoute: (planId: string, routeId: string) => Promise<any>;
  postMoverdersToRoute: (
    plan_id: string,
    trackings: Array<number>,
    route_id: string,
  ) => Promise<any>;
}

export default function useLastMileRequest(
  httpInstance: MutableRefObject<AxiosInstance | null>,
): LastMileRequest {
  // --- Cluster.ts methods ---
  const getVersionsSettings = useCallback(
    async (station: string, country: string) => {
      if (!httpInstance.current)
        throw new Error('Http instance is not initialized');
      const response = await httpInstance.current.get<Array<IVersion>>(
        '/settings/versions',
        {
          params: { station, country },
        },
      );
      return response.data;
    },
    [httpInstance],
  );

  const saveMap = useCallback(
    async (version: string, station: string, country: string, file: File) => {
      if (!httpInstance.current)
        throw new Error('Http instance is not initialized');
      const data = new FormData();
      data.append('file', file as any);
      data.append('version', version as any);
      data.append('station', station as any);
      data.append('country', country as any);
      const response = await httpInstance.current.post<IResponsePrepare>(
        '/settings/json/create',
        data,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        },
      );
      return response.data;
    },
    [httpInstance],
  );

  const saveVersionSettings = useCallback(
    async (
      version: string,
      station: string,
      country: string,
      createEmpty: boolean,
      cloneVersion: string,
    ) => {
      if (!httpInstance.current)
        throw new Error('Http instance is not initialized');
      await httpInstance.current.post<Array<any>>('/settings/versions/create', {
        version,
        station,
        country,
        createEmpty,
        cloneVersion,
      });
    },
    [httpInstance],
  );

  const getZonesSettings = useCallback(
    async (country: string, station: string, versionId: string | null) => {
      if (!httpInstance.current)
        throw new Error('Http instance is not initialized');
      const response = await httpInstance.current.get<Array<IZone>>(
        '/settings/zones',
        {
          params: { country, station, versionId },
        },
      );
      return response.data.map((zone): IZoneArea => {
        const feature = turf.polygon(zone.feature.coordinates);
        feature.properties = {
          id: zone._id,
          name: zone.name,
          color: zone.color,
          country: zone.country,
          station: zone.station,
          identity: zone.identity,
        };
        return {
          _id: zone._id,
          name: zone.name,
          color: zone.color,
          country: zone.country,
          station: zone.station,
          identity: zone.identity,
          feature: feature,
        };
      });
    },
    [httpInstance],
  );

  const storeZones = useCallback(
    async (
      country: string,
      station: string,
      areas: Array<IZoneArea>,
      versionId: string | null,
    ) => {
      if (!httpInstance.current)
        throw new Error('Http instance is not initialized');
      const zones: Array<IZone> = areas.map((area) => {
        const { feature, ...rest } = area;
        return {
          ...rest!,
          feature: {
            type: feature!.geometry.type,
            coordinates: feature!.geometry.coordinates,
          },
          geometry: feature!.geometry,
          versionId: versionId,
        } as IZone;
      });
      await httpInstance.current.post<Array<IZone>>('/settings/zones/create', {
        country,
        station,
        zones,
        versionId,
      });
    },
    [httpInstance],
  );

  const findZone = useCallback(
    async (country: string, station: string, versionId: string | null) => {
      if (!httpInstance.current)
        throw new Error('Http instance is not initialized');
      const response = await httpInstance.current.get<Array<IZone>>(
        '/settings/zones/select',
        {
          params: { country, station, versionId },
        },
      );
      return response.data as any;
    },
    [httpInstance],
  );

  const prepareKmeans = useCallback(
    async (kmeans: IForm, email: string) => {
      if (!httpInstance.current)
        throw new Error('Http instance is not initialized');
      const data = new FormData();
      if (kmeans.session_id)
        data.append('session_id', kmeans.session_id as any);
      data.append('algorithm', kmeans.algorithm as any);
      data.append('excel', kmeans.excel as any);
      data.append('exit_on_station', kmeans.exit_on_station as any);
      data.append('file', kmeans.file as any);
      data.append('plan_name', kmeans.plan_name as any);
      data.append('prefix', kmeans.prefix as any);
      data.append('days', kmeans.days as any);
      data.append('kmeans', JSON.stringify(kmeans.kmeans) as any);
      data.append('ortools', JSON.stringify(kmeans.ortools) as any);
      data.append('station', JSON.stringify(kmeans.station) as any);
      data.append('version', JSON.stringify(kmeans.version) as any);
      data.append('differential', kmeans.differential as any);
      data.append('created_by', email as any);
      const response = await httpInstance.current.post<IResponsePrepare>(
        '/cluster/prepare',
        data,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        },
      );
      return response.data;
    },
    [httpInstance],
  );

  const calculateKmeans = useCallback(
    async (keans: IForm) => {
      if (!httpInstance.current)
        throw new Error('Http instance is not initialized');
      const response = await httpInstance.current.get<IResponsePrepare>(
        `/cluster/calculate/${keans.session_id}`,
      );
      return response.data;
    },
    [httpInstance],
  );

  const calculateOrTools = useCallback(
    async (keans: IForm) => {
      if (!httpInstance.current)
        throw new Error('Http instance is not initialized');
      const response = await httpInstance.current.get<IResponsePrepare>(
        `/cluster/calculate/${keans.session_id}/or-tools`,
      );
      return response.data;
    },
    [httpInstance],
  );

  const getKmeans = useCallback(
    async (date: Date, station: IStation) => {
      if (!httpInstance.current)
        throw new Error('Http instance is not initialized');
      const response = await httpInstance.current.get('/resumen', {
        params: {
          from_date: startOfDay(date),
          to_date: endOfDay(date),
          station: station.station,
          country: station.country,
        },
      });
      return response.data;
    },
    [httpInstance],
  );

  const getPlanSummary = useCallback(
    async (plan_id: string) => {
      if (!httpInstance.current)
        throw new Error('Http instance is not initialized');
      const response = await httpInstance.current.post('/resumen/get', {
        plan_id,
      });
      return response.data;
    },
    [httpInstance],
  );

  const approvePlanSummary = useCallback(
    async (plan_id: string, approve: boolean) => {
      if (!httpInstance.current)
        throw new Error('Http instance is not initialized');
      let error = false;
      const response = await httpInstance.current
        .post('/cluster/approve-reject', { plan_id, approve })
        .catch((e) => {
          error = true;
          return e.response;
        });
      return { data: response.data, error: error };
    },
    [httpInstance],
  );

  const initOSRMService = useCallback(async () => {
    if (!httpInstance.current)
      throw new Error('Http instance is not initialized');
    await httpInstance.current.post('/cluster/wake-up-osrm');
  }, [httpInstance]);

  const prepareForInduction = useCallback(
    async (plans: Array<string>) => {
      if (!httpInstance.current)
        throw new Error('Http instance is not initialized');
      const response = await httpInstance.current.post(
        '/resumen/prepare-induction',
        { plans },
      );
      return response.data;
    },
    [httpInstance],
  );

  // --- Version.ts methods ---
  const getVersions = useCallback(
    async (country: string, station: string, includeInactive = false) => {
      if (!httpInstance.current)
        throw new Error('Http instance is not initialized');
      const r = await httpInstance.current.get<Array<Version>>(
        `/version/list/${country}/${station}?includeInactive=${includeInactive}`,
      );
      return r.data;
    },
    [httpInstance],
  );

  const disableVersion = useCallback(
    async (versionId: string) => {
      if (!httpInstance.current)
        throw new Error('Http instance is not initialized');
      const r = await httpInstance.current.patch(
        `/version/${versionId}/disable`,
      );
      return r.data;
    },
    [httpInstance],
  );

  const enableVersion = useCallback(
    async (versionId: string) => {
      if (!httpInstance.current)
        throw new Error('Http instance is not initialized');
      const r = await httpInstance.current.patch(
        `/version/${versionId}/enable`,
      );
      return r.data;
    },
    [httpInstance],
  );

  const deleteVersion = useCallback(
    async (versionId: string) => {
      if (!httpInstance.current)
        throw new Error('Http instance is not initialized');
      const r = await httpInstance.current.delete(
        `/version/${versionId}/delete`,
      );
      return r.data;
    },
    [httpInstance],
  );

  const getZones = useCallback(
    async (versionId: string) => {
      if (!httpInstance.current)
        throw new Error('Http instance is not initialized');
      const r = await httpInstance.current.get<Array<Zone>>(
        `/version/${versionId}/zones`,
      );
      return r.data;
    },
    [httpInstance],
  );

  const deleteZone = useCallback(
    async (zoneId: string) => {
      if (!httpInstance.current)
        throw new Error('Http instance is not initialized');
      const r = await httpInstance.current.delete(
        `/version/zone/${zoneId}/delete`,
      );
      return r.data;
    },
    [httpInstance],
  );

  const disolveZones = useCallback(
    async (zones: Array<string>, name: string) => {
      if (!httpInstance.current)
        throw new Error('Http instance is not initialized');
      const r = await httpInstance.current.post(`/version/zone/disolve`, {
        zones,
        name,
      });
      return r.data;
    },
    [httpInstance],
  );

  const createVersion = useCallback(
    async (name: string, file: File, station: string, country: string) => {
      if (!httpInstance.current)
        throw new Error('Http instance is not initialized');
      const data = new FormData();
      data.append('name', name);
      data.append('file', file);
      data.append('station', station);
      data.append('country', country);
      const r = await httpInstance.current.post(`/version/create`, data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return r.data;
    },
    [httpInstance],
  );

  // --- Routing.ts methods ---
  const getRoutingVerification = useCallback(
    async (
      plan_id: string,
      plan_name: string,
      prefix: string,
      station: string,
      country: string,
      mode: calculation_mode,
      universe: Array<universe_mode>,
      sizes: Array<sizes_enum>,
      version: IVersion,
      vehicles: Partial<IVehiclesCapacities>,
      file: File | null,
      simulationTime: number,
      routeTime: number,
      fusion: number,
      algorithm: algorithms,
      max_distance = 100000,
      service_time = 180,
      preset = 'standard_parcel',
    ) => {
      if (!httpInstance.current)
        throw new Error('Http instance is not initialized');
      const data = new FormData();
      data.append('plan_id', plan_id);
      data.append('plan_name', plan_name);
      data.append('prefix', prefix);
      data.append('station', station);
      data.append('country', country);
      data.append('mode', mode.toString());
      data.append('universe', JSON.stringify(universe));
      data.append('sizes', JSON.stringify(sizes));
      data.append('version', version._id);
      data.append('vehicles', JSON.stringify(vehicles));
      data.append('file', file as any);
      data.append('simulation_time', simulationTime.toString());
      data.append('route_time', routeTime.toString());
      data.append('max_distance', max_distance.toString());
      data.append('service_time', service_time.toString());
      data.append('preset', preset);
      data.append('fusion', fusion.toString());
      data.append('algorithm', algorithm.toString());

      // Validation logic from original file omitted for brevity, assuming caller handles it or I add it here?
      // The original file had validation inside. I'll skip it for now or assume it's done before calling.
      // Actually, good practice to keep it if it was there. But hooks usually just do the request.

      const response = await httpInstance.current.post<Statistic>(
        '/routing/verify-plan',
        data,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        },
      );
      return response.data;
    },
    [httpInstance],
  );

  const prepareSimulation = useCallback(
    async (plan_id: string) => {
      if (!httpInstance.current)
        throw new Error('Http instance is not initialized');
      const response = await httpInstance.current.post<any>(
        '/routing/process-plan',
        { plan_id },
      );
      return response.data;
    },
    [httpInstance],
  );

  const startSimulation = useCallback(
    async (plan_id: string, user: string) => {
      if (!httpInstance.current)
        throw new Error('Http instance is not initialized');
      const response = await httpInstance.current.post<any>(
        '/routing/simulate',
        { plan_id, user },
      );
      return response.data;
    },
    [httpInstance],
  );

  const startKSimulation = useCallback(
    async (plan_id: string, user: string) => {
      if (!httpInstance.current)
        throw new Error('Http instance is not initialized');
      const response = await httpInstance.current.post<any>(
        '/routing/process-k-plan',
        { plan_id, user },
      );
      return response.data;
    },
    [httpInstance],
  );

  const cleanUp = useCallback(
    async (plan_id: Array<string>) => {
      if (!httpInstance.current)
        throw new Error('Http instance is not initialized');
      await httpInstance.current.post<any>('/routing/process-k-plan/clean-up', {
        plan_id,
      });
    },
    [httpInstance],
  );

  const acceptPlan = useCallback(
    async (plan_id: string, station: string) => {
      if (!httpInstance.current)
        throw new Error('Http instance is not initialized');
      const response = await httpInstance.current.post<any>(
        `/routing/ui/plan/${plan_id}/status`,
        {
          status: 'accepted',
          station: station,
        },
      );
      return response.data;
    },
    [httpInstance],
  );

  const rejectPlan = useCallback(
    async (plan_id: string) => {
      if (!httpInstance.current)
        throw new Error('Http instance is not initialized');
      const response = await httpInstance.current.post<any>(
        `/routing/ui/plan/${plan_id}/status`,
        {
          status: 'rejected',
        },
      );
      return response.data;
    },
    [httpInstance],
  );

  const getPlan = useCallback(
    async (plan_id: string) => {
      if (!httpInstance.current)
        throw new Error('Http instance is not initialized');
      const response = await httpInstance.current.get<Simulation>(
        `/routing/ui/plan/${plan_id}`,
      );
      return response.data;
    },
    [httpInstance],
  );

  const getPlans = useCallback(
    async (pagination: any) => {
      if (!httpInstance.current)
        throw new Error('Http instance is not initialized');
      const response = await httpInstance.current.post<any>(
        `/routing/ui/simulations`,
        pagination,
      );
      return response.data;
    },
    [httpInstance],
  );

  const getRoutes = useCallback(
    async (simulations: Array<string>) => {
      if (!httpInstance.current)
        throw new Error('Http instance is not initialized');
      const response = await httpInstance.current.post<Array<InductionRoute>>(
        `/routing/ui/routes`,
        { simulations },
      );
      return response.data;
    },
    [httpInstance],
  );

  const setInduct = useCallback(
    async (plan_id: string, route: string, tracking_id: number) => {
      if (!httpInstance.current)
        throw new Error('Http instance is not initialized');
      const response = await httpInstance.current.post<Array<InductionRoute>>(
        `/routing/ops/induct`,
        {
          plan_id,
          route,
          tracking_id,
        },
      );
      return response.data;
    },
    [httpInstance],
  );

  const getInductProgress = useCallback(
    async (plan_id: Array<string>, routes: Array<string>) => {
      if (!httpInstance.current)
        throw new Error('Http instance is not initialized');
      const response = await httpInstance.current.post<Array<InductionRoute>>(
        `/routing/ops/induct/progress`,
        {
          plan_id,
          routes,
        },
      );
      return response.data;
    },
    [httpInstance],
  );

  const postSplitRoute = useCallback(
    async (
      planId: string,
      routeId: string,
      vehicles: Partial<IVehiclesCapacities>,
    ) => {
      if (!httpInstance.current)
        throw new Error('Http instance is not initialized');
      const response = await httpInstance.current.post<any>(
        `/routing/ops/split`,
        { planId, routeId, vehicles },
      );
      return response.data;
    },
    [httpInstance],
  );

  const postJoinRoutes = useCallback(
    async (planId: string, routeIds: Array<string>) => {
      if (!httpInstance.current)
        throw new Error('Http instance is not initialized');
      const response = await httpInstance.current.post<any>(
        `/routing/ops/join`,
        { planId, routeIds },
      );
      return response.data;
    },
    [httpInstance],
  );

  const postDisableRoute = useCallback(
    async (planId: string, routeId: string) => {
      if (!httpInstance.current)
        throw new Error('Http instance is not initialized');
      const response = await httpInstance.current.post<any>(
        `/routing/ops/disable`,
        { planId, routeId },
      );
      return response.data;
    },
    [httpInstance],
  );

  const postMoverdersToRoute = useCallback(
    async (plan_id: string, trackings: Array<number>, route_id: string) => {
      if (!httpInstance.current)
        throw new Error('Http instance is not initialized');
      const response = await httpInstance.current.post<any>(
        `/routing/ops/move-orders`,
        { plan_id, trackings, route_id },
      );
      return response.data;
    },
    [httpInstance],
  );

  return {
    getVersionsSettings,
    saveMap,
    saveVersionSettings,
    getZonesSettings,
    storeZones,
    findZone,
    prepareKmeans,
    calculateKmeans,
    calculateOrTools,
    getKmeans,
    getPlanSummary,
    approvePlanSummary,
    initOSRMService,
    prepareForInduction,
    getVersions,
    disableVersion,
    enableVersion,
    deleteVersion,
    getZones,
    deleteZone,
    disolveZones,
    createVersion,
    getRoutingVerification,
    prepareSimulation,
    startSimulation,
    startKSimulation,
    cleanUp,
    acceptPlan,
    rejectPlan,
    getPlan,
    getPlans,
    getRoutes,
    setInduct,
    getInductProgress,
    postSplitRoute,
    postJoinRoutes,
    postDisableRoute,
    postMoverdersToRoute,
  };
}
